import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import type { MachineNameAndSourceId } from "@common/types/server-list-types";
import { CheckBoxWithText } from "@src/components/atoms/check-box-with-text/check-box-with-text";
import {
  MACHINE_OVERVIEW_FIXED_BINDING_KEYS,
  MachineOverviewDisplayMode,
  METRIC_SLOTS,
  REQUIRED_METRIC_SLOTS,
  STATUS_COLOR_HEX,
  HEX_TO_STATUS_TOKEN,
  STATUS_SWATCHES,
  DEFAULT_PROGRESS_START_HEX,
  DEFAULT_PROGRESS_END_HEX,
  metricFieldPrefix,
  metricSlotField,
} from "@src/components/organisms/widgets/machine-overview/machine-overview-widget-config";
import {
  ColorSwatchPicker,
  TREND_LINE_PALETTE,
} from "@src/components/atoms/color-swatch-picker/color-swatch-picker";
import { PillNavigation } from "@src/components/atoms/pill-navigation/pill-navigation";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import {
  GAUGE_LOOKBACK_MS,
  DEFAULT_POLLING_INTERVAL,
  buildPollingOptions,
  durationToIso,
  findPollingOption,
  isoToDurationMs,
} from "@src/components/organisms/widgets/constants";
import {
  MachineOverviewMetric,
  MachineOverviewMetricCycleUnit,
  MachineOverviewMetricKind,
  MachineOverviewMetricSlot,
  MachineOverviewRow,
  MachineOverviewStatusEntry,
  QueryBinding,
  Widget,
  WidgetSemantics,
} from "@src/types/dashboards";
import {
  selectUnitsById,
  selectUnitsStatus,
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { findUnitByReference as findUnitByReferenceInUnits } from "@src/cloud-connect/units/utils/find-unit-by-reference";
import { unitOptionLabelOf } from "@src/cloud-connect/units/utils/unit-option-label-of";
import type { UnitDefinition } from "@src/types/units";
import {
  buildScaleTransforms,
  mergeTransforms,
  transformsKey,
} from "@src/components/organisms/widgets/utils/value-scaling";
import { parseUserNumber } from "@src/components/organisms/widgets/utils/parse-user-number";
import { useSaveWidget } from "../hooks/use-save-widget";
import { RootState } from "@src/reducers/store";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";
import {
  getNumericNodeUnit,
  toNumericAndStringNodeOptions,
  toNumericNodeOptions,
} from "@src/components/organisms/widgets/node-options";
import { getWidgetDataSource } from "@src/components/organisms/widgets/widget-query";
import { DropAreaAddImage } from "@src/components/atoms/drop-area-add-image/drop-area-add-image";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import Spinner from "@src/components/atoms/spinner/spinner";
import deleteRedIcon from "@src/assets/svg/delete-red-icon.svg";
import styles from "./machine-overview-editor.module.scss";
import { ScaleInput } from "@src/components/atoms/scale-input/scale-input";
import { DashboardEditWidgetDrawerDropdown } from "@src/components/atoms/dashboard-edit-widget-drawer-dropdown/dashboard-edit-widget-drawer-dropdown";
import closeIcon from "@src/assets/svg/close-icon.svg";
import { buildDurationOptions } from "../../../widgets/utils/build-duration-options";
import { findDurationOption } from "../../../widgets/utils/find-duration-options";
/** Classifies the status-entry value problem so validate() can pick the
 *  right i18n message. exact reads entry.value (string|number); interval
 *  reads entry.min+entry.max (separate numeric fields, not a serialized
 *  "X..Y" — see matchStatusEntry in machine-overview-widget-config.ts).
 *  An inverted interval (min > max) returns "inverted" — it would save
 *  silently but match no rawValue ever, so the warning fires at edit
 *  time instead of failing at the dashboard. */
const checkStatusEntryValue = (
  entry: MachineOverviewStatusEntry,
): "valid" | "missing" | "inverted" => {
  if (entry.mode === "interval") {
    if (typeof entry.min !== "number" || !Number.isFinite(entry.min))
      return "missing";
    if (typeof entry.max !== "number" || !Number.isFinite(entry.max))
      return "missing";
    if (entry.min > entry.max) return "inverted";
    return "valid";
  }
  const raw = entry.value;
  if (raw == null) return "missing";
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? "valid" : "missing";
  }
  return String(raw).trim() !== "" ? "valid" : "missing";
};

export interface MachineOverviewEditorProps {
  widget: Widget;
  isPendingWidget: boolean;
  onClose: () => void;
}

const StatusRowEditor = ({
  index,
  entry,
  onChange,
  onRemove,
  removable = true,
  displayMode = "badge",
}: {
  index: number;
  entry: MachineOverviewStatusEntry;
  onChange: (next: MachineOverviewStatusEntry) => void;
  onRemove: () => void;
  /** First entry is the pre-seeded baseline — not removable (no ×). */
  removable?: boolean;
  /** Background mode hides the label input — the card still carries the
   *  data so a flip back to badge mode doesn't lose what's already
   *  typed (matches the user's "without losing the setups" ask). */
  displayMode?: MachineOverviewDisplayMode;
}) => {
  const i18n = useI18N();
  const NS = "modules.machineOverview";

  const returnText = (index: number, translations: string): string =>
    i18n(translations).replace("{{ index }}", String(index + 1));

  return (
    <div className={styles["machine-overview-editor__status-row"]}>
      {displayMode === "badge" && (
        <Input2025
          inputPlaceHolder={returnText(index, `${NS}.statusPlaceholder`)}
          labelText={returnText(index, `${NS}.statusLabel`)}
          inputValue={entry.label}
          onChangeFunc={(e) => onChange({ ...entry, label: e.target.value })}
          inputVariant="dark-background"
        />
      )}
      {/* Color picker — ColorSwatchPicker, same atom other drawer editors use
         for counter-number / static-tool. Token persists in storage; hex is
         derived at render time, mapped back to the token on `onChange`. A
         status entry always carries an explicit token, so its colour is always
         one of `STATUS_SWATCHES`. */}
      <ColorSwatchPicker
        label={i18n(`${NS}.statusColorLabel`)}
        value={STATUS_COLOR_HEX[entry.color_token]}
        swatches={STATUS_SWATCHES}
        onChange={(hex) =>
          onChange({
            ...entry,
            color_token: HEX_TO_STATUS_TOKEN[hex] ?? entry.color_token,
          })
        }
      />
      {/* Mode label + toggle — same field-label style as the rest of the
         drawer; PillNavigation matches the font-size + alignment pickers.
         onChange preserves the existing button behavior: → exact: keep
         `value`, clear min/max. → interval: keep min/max, clear `value`. */}
      <span className={styles["machine-overview-editor__field-label"]}>
        {i18n(`${NS}.statusModeLabel`)}
      </span>
      <PillNavigation
        options={[
          {
            name: i18n(`${NS}.statusModeExact`),
            link: "exact",
            render: true,
          },
          {
            name: i18n(`${NS}.statusModeInterval`),
            link: "interval",
            render: true,
          },
        ]}
        activeView={entry.mode}
        border={true}
        onChangeCallback={(value) =>
          value === "exact"
            ? onChange({
                ...entry,
                mode: "exact",
                value:
                  typeof entry.value === "number"
                    ? entry.value
                    : (entry.value as string) ?? "",
                min: undefined,
                max: undefined,
              })
            : onChange({
                ...entry,
                mode: "interval",
                value: undefined,
                min: typeof entry.min === "number" ? entry.min : 0,
                max: typeof entry.max === "number" ? entry.max : 0,
              })
        }
      />
      {entry.mode === "exact" ? (
        <Input2025
          inputPlaceHolder={i18n(`${NS}.statusValuePlaceholder`)}
          labelText={i18n(`${NS}.statusValue`)}
          inputValue={
            entry.value === undefined || entry.value === null
              ? ""
              : String(entry.value)
          }
          onChangeFunc={(e) => {
            const raw = e.target.value;
            const looksNumeric =
              raw.length > 0 && raw !== "-" && /^-?\d*\.?\d*$/.test(raw);
            onChange({
              ...entry,
              value: looksNumeric ? Number(raw) : raw,
            });
          }}
          inputVariant="dark-background"
        />
      ) : (
        <div className={styles["machine-overview-editor__interval-pair"]}>
          <Input2025
            inputPlaceHolder={i18n(`${NS}.statusMin`)}
            labelText={i18n(`${NS}.statusMin`)}
            inputValue={String(entry.min ?? 0)}
            onChangeFunc={(e) => {
              const v = Number(e.target.value);
              onChange({ ...entry, min: Number.isFinite(v) ? v : 0 });
            }}
            inputVariant="dark-background"
          />
          <Input2025
            inputPlaceHolder={i18n(`${NS}.statusMax`)}
            labelText={i18n(`${NS}.statusMax`)}
            inputValue={String(entry.max ?? 0)}
            onChangeFunc={(e) => {
              const v = Number(e.target.value);
              onChange({ ...entry, max: Number.isFinite(v) ? v : 0 });
            }}
            inputVariant="dark-background"
          />
        </div>
      )}
      {removable && (
        <button
          type="button"
          className={styles["machine-overview-editor__remove-entry"]}
          onClick={onRemove}
          aria-label={i18n(`${NS}.removeStatusMapping`)}
        >
          <img src={closeIcon} alt="" />
        </button>
      )}
    </div>
  );
};

/** Inline picker for the metric kind (Remaining Time / Quality).
 *  Mirrors the language-toggle pattern: a button that opens a
 *  custom-styled popover (NOT the browser's native `<select>`
 *  popover, which carries OS-specific styling the user already
 *  flagged as ugly). Click-outside closes; choosing an item
 *  closes; clicking the button itself toggles. One instance per
 *  metric card — each carries its own open/close state, so
 *  opening metric A's popover doesn't close metric B's. */
const MetricTypeDropdown = ({
  value,
  options,
  onChange,
  ariaId,
}: {
  value: string;
  options: { key: string; label: string }[];
  onChange: (value: string) => void;
  ariaId: string;
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useClickOutsideListener(popoverRef, () => setOpen(false), open);
  const selectedOption = options.find((o) => o.key === value) ?? options[0];
  if (!selectedOption) return null;
  return (
    <div
      ref={popoverRef}
      className={styles["machine-overview-editor__metric-type-dropdown"]}
    >
      <button
        type="button"
        id={ariaId}
        className={styles["machine-overview-editor__metric-type-button"]}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption.label}</span>
      </button>
      {open && (
        <ul
          className={styles["machine-overview-editor__metric-type-list"]}
          role="listbox"
          aria-labelledby={ariaId}
        >
          {options.map((opt) => (
            <li key={opt.key}>
              <button
                type="button"
                className={styles["machine-overview-editor__metric-type-item"]}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                aria-selected={opt.key === value}
                role="option"
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Cycle-time unit dropdown — same control family as `MetricTypeDropdown`
 *  (same `__metric-type-*` SCSS so it visually matches the kind
 *  picker) but with one key difference: when no unit is picked, the
 *  button renders `emptyLabel` (e.g. "Select cycle time unit")
 *  instead of falling back to `options[0]`, AND the popover list
 *  contains ONLY the three real units — no placeholder option. The
 *  caller passes the labels already-resolved (this component sits
 *  outside the parent's closure so it can't read `i18n`/`NS`
 *  directly). `onChange` emits a real `MachineOverviewMetricCycleUnit`
 *  string, never an empty placeholder; the parent handles the
 *  no-op-on-empty guard. */
const CycleTimeUnitDropdown = ({
  metric,
  options,
  emptyLabel,
  ariaId,
  onChange,
}: {
  metric: MachineOverviewMetric;
  options: { key: MachineOverviewMetricCycleUnit; label: string }[];
  emptyLabel: string;
  ariaId: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useClickOutsideListener(popoverRef, () => setOpen(false), open);
  const selected = options.find((o) => o.key === metric.cycle_unit);
  const buttonLabel = selected?.label ?? emptyLabel;
  return (
    <div
      ref={popoverRef}
      className={styles["machine-overview-editor__metric-type-dropdown"]}
    >
      <button
        type="button"
        id={ariaId}
        className={styles["machine-overview-editor__metric-type-button"]}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{buttonLabel}</span>
      </button>
      {open && (
        <ul
          className={styles["machine-overview-editor__metric-type-list"]}
          role="listbox"
          aria-labelledby={ariaId}
        >
          {options.map((opt) => (
            <li key={opt.key}>
              <button
                type="button"
                className={styles["machine-overview-editor__metric-type-item"]}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                aria-selected={opt.key === metric.cycle_unit}
                role="option"
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const MachineOverviewEditor = ({
  widget,
  isPendingWidget,
  onClose,
}: MachineOverviewEditorProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const NS = "modules.machineOverview";

  const units = useSelector(selectUnitsById);
  const unitsStatus = useSelector(selectUnitsStatus);
  const isLoadingUnits = unitsStatus === "idle" || unitsStatus === "loading";
  const dashboards = useSelector(
    (state: RootState) => state.dashboards.dashboards,
  );
  const savedDataSource = useMemo(
    () => getWidgetDataSource(widget.query),
    [widget.query],
  );

  const [title, setTitle] = useState(widget.title);
  // The machine the widget reads, seeded from the group its saved bindings
  // point at. A widget whose first binding carries no address stays unset —
  // same as the drawer's prefill, and the user picks the machine again.
  const [machine, setMachine] = useState<MachineNameAndSourceId | null>(() =>
    savedDataSource
      ? { name: savedDataSource.group, source_id: savedDataSource.group }
      : null,
  );
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [numericNodeOptions, setNumericNodeOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [numericAndStringNodeOptions, setNumericAndStringNodeOptions] =
    useState<MachineNameAndSourceId[]>([]);
  const [nodeUnitReferenceByNodeSourceId, setNodeUnitReferenceByNodeSourceId] =
    useState<Record<string, number | null>>({});
  const [isLoadingMachines, setIsLoadingMachines] = useState(true);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [lookbackMs, setLookbackMs] = useState<number | null>(
    isoToDurationMs(widget.query?.window?.duration),
  );
  const [pollingInterval, setPollingInterval] = useState<string | null>(
    widget.query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
  );
  // Multipart image intent: a File replaces the asset, `null` keeps it,
  // `undefined` clears it (see `SaveWidgetOptions.imageFile`).
  const [imageFile, setImageFile] = useState<File | null | undefined>(null);
  const [dashboardId, setDashboardId] = useState<string | null>(
    typeof widget.presentation?.dashboard_id === "string"
      ? widget.presentation.dashboard_id
      : null,
  );
  // Published dashboards only. A saved id that is no longer published is still
  // shown as `{ name: id }` so the user can see and clear it.
  const dashboardOptions = useMemo(
    () =>
      dashboards
        .filter((d) => d.status === "published")
        .map((d) => ({ name: d.name, source_id: d.id })),
    [dashboards],
  );
  // Resolves a `Timeseries.unit` (OPC-UA reference number) to a
  // `UnitDefinition` from the global units slice. Same lookup the
  // drawer's single-machine + multi-line fields use (see
  // edit-widget-drawer-modal.tsx `findUnitByReference`). Mirrored
  // here so the per-row autoset effect below can resolve a unit-id
  // straight from the active-timeseries record without bouncing
  // through the parent.
  const findUnitByReference = (
    reference: number | string | null,
  ): UnitDefinition | null => findUnitByReferenceInUnits(units, reference);
  // Unit options once per `units` change — reuses the same
  // "name (symbol)" label the drawer's unit dropdown renders, so
  // per-row unit pickers look identical to the gauge / trend-line /
  // multi-line unit pickers. `source_id` is the unit's `unit_id`
  // (stable cross-catalog id) so the picker's selected option can
  // be persisted straight onto `row.unit` without remapping and a
  // later `getUnitById(units, row.unit)` resolves the same value
  // both at save time and at render time.
  const unitOptions = useMemo(
    () =>
      Object.values(units).map((value) => {
        const text =
          value.unit_text?.eng ?? Object.values(value.unit_text ?? {})[0];
        return {
          name: unitOptionLabelOf(text, value.unit_id),
          source_id: value.unit_id,
        };
      }),
    [units],
  );

  // Editor state shape:
  //   - `nodeByRole`: field → picked OPC-UA node (null until user picks).
  //     Seeded from `widget.query.bindings` so reopening a widget
  //     pre-populates the dropdowns with the saved addresses. Keys by
  //     field so it's transparent across the 3 fixed slots AND ids in
  //     the dynamic row list (whose `field` is the join key to that
  //     row's binding).
  //   - `rows`: ordered MachineOverviewRow[]. Seeded from
  //     `widget.encoding.rows` (BE-copied template defaults + any past
  //     user edits) when present; legacy widgets that carry no
  //     `encoding.rows` are synthesized from `widget.query.bindings`
  //     with deterministic ids (so a save cycle migrates them forward
  //     and gives them stable id-keyed row_labels).
  //   - `rowLabels`: keyed by row.id for new rows; legacy
  //     field-keyed overrides (from widgets that pre-dated
  //     `encoding.rows`) are still keyed by field and picked up via
  //     fallback in the renderer. Cycles/progress labels stay
  //     field-keyed here.
  const FIXED_SETUP_FIELDS: Set<string> = new Set(
    MACHINE_OVERVIEW_FIXED_BINDING_KEYS,
  );
  const [nodeByRole, setNodeByRoleRaw] = useState<
    Record<string, MachineNameAndSourceId | null>
  >(() => {
    const out: Record<string, MachineNameAndSourceId | null> = {};
    // Fixed slots always exist (null until the user picks a node).
    for (const k of MACHINE_OVERVIEW_FIXED_BINDING_KEYS) out[k] = null;
    for (const b of widget.query?.bindings ?? []) {
      if (!b.field || !b.address) continue;
      // Initialize with placeholder both fields referencing the saved
      // address; the lookup effect below swaps name with the actual node's
      // human label when numericAndStringNodeOptions resolve. Without
      // this seed, reopening
      // a configured widget renders empty pickers and the next save
      // clobbers the saved bindings with empty addresses.
      out[b.field] = { name: b.address, source_id: b.address };
    }
    return out;
  });
  // 2-arg wrapper around `setNodeByRoleRaw`. Call sites use inline
  // `(n) => setNodeByRole("good", n)` because React's setState is 1-arg.
  const setNodeByRole = (
    role: string,
    n: MachineNameAndSourceId | null,
  ): void => {
    setNodeByRoleRaw((prev) => ({ ...prev, [role]: n }));
  };
  // Slot setter — a metric's slot binding lives on `nodeByRole` under
  // the field name produced by `metricSlotField(id, slot)`. Centralised
  // so getSnapshot, validate, and the JSX agree on the key shape
  // (changing one without the other would silently orphan bindings on
  // save). The getter is inlined at call sites to keep it closure-
  // bound to the live `nodeByRole` for exhaustive-deps without
  // re-introducing a stable wrapper per render.
  const setMetricSlot = (
    metricId: string,
    slot: MachineOverviewMetricSlot,
    n: MachineNameAndSourceId | null,
  ): void => {
    setNodeByRoleRaw((prev) => ({
      ...prev,
      [metricSlotField(metricId, slot)]: n,
    }));
  };

  // Ordered list of dynamic value rows. Seeded from `widget.encoding.rows`
  // (the source of truth that the BE copies from the template when the
  // widget is added); legacy widgets synthesize from `query.bindings` so
  // the editor renders whatever is bound instead of leaking an empty
  // list to a pre-migration widget. Brand-new widgets with neither rows
  // nor bindings land on an empty list — the user clicks "Add row" to
  // populate.
  const [rows, setRows] = useState<MachineOverviewRow[]>(() => {
    if (widget.encoding?.rows && widget.encoding.rows.length > 0) {
      return widget.encoding.rows;
    }
    const out: MachineOverviewRow[] = [];
    let idx = 0;
    for (const b of widget.query?.bindings ?? []) {
      if (!b.field || FIXED_SETUP_FIELDS.has(b.field)) continue;
      // Skip metric slot bindings — those live on `widget.encoding.metrics`
      // and are surfaced via the metrics UI, NOT as user-named math rows.
      // Without this filter a saved metric would leak a "metric_…_cycle_time"
      // row alongside its metric card AND the save would emit a phantom
      // duplicate binding the renderer reads as a math row that nothing
      // else resolves.
      if (b.field.startsWith("metric_")) continue;
      out.push({
        id: `legacy-r${idx}-${b.field}`,
        field: b.field,
        label: b.field,
      });
      idx++;
    }
    return out;
  });

  // Per-row label overrides + the cycles/progress field-keyed labels.
  // New-row overrides keyed by row.id; legacy widget overrides might
  // still be field-keyed (no migration needed — the renderer falls back
  // through rowLabels[row.field] before i18n/default).
  const [rowLabels, setRowLabels] = useState<Record<string, string>>(() => ({
    ...widget.presentation?.row_labels,
  }));
  // Status entries — at least one must always exist (the first is the
  // pre-seeded baseline and cannot be removed). New widgets (no saved
  // entries) get one starter entry.
  const [statusEntries, setStatusEntries] = useState<
    MachineOverviewStatusEntry[]
  >(() => {
    const sem = widget.semantics as {
      status_entries?: MachineOverviewStatusEntry[];
    } | null;
    const saved =
      sem && Array.isArray(sem.status_entries) ? sem.status_entries : [];
    return saved.length > 0
      ? saved
      : [
          {
            label: "",
            color_token: "white" as const,
            mode: "exact" as const,
            value: "",
          },
        ];
  });
  // How the matched status is rendered — seeded from the saved widget so
  // reopening a background-mode widget keeps its mode. Legacy widgets
  // (no status_display_mode key) fall back to "badge" (the original
  // behavior, no migration needed).
  const [statusDisplayMode, setStatusDisplayMode] =
    useState<MachineOverviewDisplayMode>(() => {
      const pres = widget.presentation as {
        status_display_mode?: MachineOverviewDisplayMode;
      } | null;
      return pres?.status_display_mode === "background"
        ? "background"
        : "badge";
    });
  // User-picked gradient stops for the progress bar fill. Always a
  // hex — no `null` branch, no Reset side-effect on the wire beyond
  // setting the default hex. Seed falls back to `DEFAULT_*_HEX` so a
  // widget saved before this feature (no presentation keys at all)
  // opens with both swatches highlighted as the default rather than
  // showing "no pick".
  const [progressColorStart, setProgressColorStart] = useState<string>(() => {
    const raw = (
      widget.presentation as { progress_color_start?: unknown } | null
    )?.progress_color_start;
    return typeof raw === "string" && raw.length > 0
      ? raw
      : DEFAULT_PROGRESS_START_HEX;
  });
  const [progressColorEnd, setProgressColorEnd] = useState<string>(() => {
    const raw = (widget.presentation as { progress_color_end?: unknown } | null)
      ?.progress_color_end;
    return typeof raw === "string" && raw.length > 0
      ? raw
      : DEFAULT_PROGRESS_END_HEX;
  });

  // Metric cards — ordered list of (kind + insertion order). Slot
  // bindings ride on `nodeByRole` under the field prefix
  // `metric_<id>_<slot>` (reusing the seeded `nodeByRole` map so an
  // existing extra-binding lookup effect handles the human-name
  // resolution without a parallel mechanism). Seeded from
  // `widget.encoding.metrics` sorted by order so the user sees them in
  // the same sequence they were saved. Empty list = no metrics.
  const [metrics, setMetrics] = useState<MachineOverviewMetric[]>(() => {
    const saved = widget.encoding?.metrics;
    if (!saved || saved.length === 0) return [];
    return saved.slice().sort((a, b) => a.order - b.order);
  });

  // Per-field scale inputs, keyed by binding field (`good`, `total`, and
  // each dynamic row's field). Kept as text so a German comma decimal
  // types naturally; parsed at snapshot time. Seeded from the saved
  // `encoding.transforms`; a field with no entry shows the default `1`.
  const [scaleByField, setScaleByField] = useState<Record<string, string>>(
    () => {
      const out: Record<string, string> = {};
      for (const t of widget.encoding?.transforms ?? []) {
        if (t.op === "scale" && t.value != null) out[t.field] = String(t.value);
      }
      return out;
    },
  );
  // Rows whose "Show unit" was ticked but whose node has no assignable unit
  // yet. `row.unit` alone cannot represent that state (it stays null until a
  // unit is picked), so the checkbox/dropdown would otherwise never appear for
  // a unitless node.
  const [unitExpandedRows, setUnitExpandedRows] = useState<
    Record<string, boolean>
  >({});
  const showsUnit = (row: MachineOverviewRow): boolean =>
    row.unit != null || unitExpandedRows[row.id] === true;
  const scaleInputFor = (field: string): string => scaleByField[field] ?? "1";
  const setScaleFor = (field: string, raw: string): void =>
    setScaleByField((prev) => ({ ...prev, [field]: raw }));
  // Fields whose scale this editor owns, accumulated across the session
  // (seeded from the loaded rows + fixed counters, extended on each Add
  // row). A field stays owned after its row is deleted, so a save drops
  // that field's stale `encoding.transforms` entry instead of preserving
  // it as "unrelated" and resurrecting it when the field name is reused.
  const ownedScaleFieldsRef = useRef<Set<string>>(
    (() => {
      const owned = new Set<string>(["good", "total"]);
      const savedRows = widget.encoding?.rows;
      if (savedRows && savedRows.length > 0) {
        for (const row of savedRows) owned.add(row.field);
      } else {
        for (const b of widget.query?.bindings ?? []) {
          if (!b.field || FIXED_SETUP_FIELDS.has(b.field)) continue;
          if (b.field.startsWith("metric_")) continue;
          owned.add(b.field);
        }
      }
      return owned;
    })(),
  );

  // Once numericAndStringNodeOptions populate, replace each role's
  // placeholder {name} with the actual human label from the matching
  // option (keep source_id = address so MachineNameDropdown's selection
  // logic stays consistent). The WIDER list is the lookup source so
  // saved bindings of either numeric- or string-typed sources get
  // their human label resolved on reopen.
  useEffect(() => {
    if (
      !numericAndStringNodeOptions ||
      numericAndStringNodeOptions.length === 0
    )
      return;
    setNodeByRoleRaw((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const field of Object.keys(next)) {
        const current = next[field];
        if (!current || !current.source_id) continue;
        const match = numericAndStringNodeOptions.find(
          (n) => n.source_id === current.source_id,
        );
        if (match && match.name !== current.name) {
          next[field] = match;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [numericAndStringNodeOptions]);

  // Clear stale node picks when the user switches machine. The drawer keys
  // this editor by widget id, so a widget switch remounts and re-seeds from
  // the new widget's bindings — only an explicit pick needs the reset.
  const handleMachineChange = (next: MachineNameAndSourceId | null): void => {
    if (next?.source_id === machine?.source_id) return;
    setMachine(next);
    setNodeByRoleRaw((prev) =>
      Object.fromEntries(Object.keys(prev).map((k) => [k, null])),
    );
  };

  // Auto-fill the unit of every row whose checkbox is on (`row.unit !=
  // null`) when the row's picked node changes. The candidate resolves
  // through the cached `nodeUnitReferenceByNodeSourceId` (populated by
  // the drawer once per machine pick in the same
  // `getActiveTimeseriesUseCase` call that produces
  // `numericAndStringNodeOptions`), so no extra network round-trip is
  // introduced. Rows that opted out (unit == null) stay null — the
  // user hasn't decided to show a unit, so the spec's "otherwise let
  // the user pick" applies without us second-guessing.
  //
  // Distinguishes "node has unit ref" (auto-fill) from "node has no
  // unit ref" (preserve) — the user's manual pick must NOT clear
  // when binding to a unitless node. A saved widget that named a
  // unit for a node whose `Timeseries.unit` later became null then
  // round-trips cleanly.
  useEffect(() => {
    setRows((prevRows) => {
      let dirty = false;
      const next = prevRows.map((row) => {
        // Auto-fill applies to every row that is SHOWING a unit — including
        // one just expanded whose node has not been picked yet.
        if (!showsUnit(row)) return row;
        const picked = nodeByRole[row.field] ?? null;
        if (picked == null) return row;
        const ref = nodeUnitReferenceByNodeSourceId[picked.source_id] ?? null;
        if (ref == null) return row;
        const candidate = findUnitByReference(ref)?.unit_id ?? null;
        // The node names a unit but the catalog cannot resolve it (yet):
        // keep whatever the row has rather than clearing it to null.
        if (candidate == null) return row;
        if (candidate !== row.unit) {
          dirty = true;
          return { ...row, unit: candidate };
        }
        return row;
      });
      return dirty ? next : prevRows;
    });
    // `units` is a dep so a row ticked before the catalog loads auto-fills
    // once it arrives. `showsUnit`/`findUnitByReference` close over the
    // current render's values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeByRole, nodeUnitReferenceByNodeSourceId, unitExpandedRows, units]);

  // Load machines once.
  useEffect(() => {
    setIsLoadingMachines(true);
    getAllSourcesListUseCase().then((result) => {
      if (result.success && result.data) {
        setMachineOptions(
          Object.values(result.data).map((s) => ({
            name: s.name,
            source_id: s.source_id,
          })),
        );
      } else {
        setMachineOptions([]);
        console.error("Failed to load machines:", result.error);
      }
      setIsLoadingMachines(false);
    });
  }, []);

  // Units come from the global slice (loaded at dashboards-load). Lazy
  // fallback: if the slice was never attempted, fetch once so the unit
  // dropdown isn't empty.
  const unitsAttempted = useRef(false);
  useEffect(() => {
    if (unitsStatus !== "idle" || unitsAttempted.current) return;
    unitsAttempted.current = true;
    dispatch(unitsRequested());
    getUnitsUseCase().then((result) => {
      if (result.success && result.data) {
        dispatch(unitsLoaded(result.data));
      } else {
        dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
      }
    });
  }, [unitsStatus, dispatch]);

  // The sources list loads asynchronously, so the seed hands the machine id
  // forward and this effect swaps in the real name once the options arrive.
  useEffect(() => {
    if (machineOptions.length === 0) return;
    const machineId = savedDataSource?.group ?? widget.query?.source;
    if (!machineId) return;
    const match = machineOptions.find((m) => m.source_id === machineId);
    if (!match) return;
    setMachine((prev) => {
      if (!prev) return prev;
      // If the user is currently looking at a DIFFERENT machine, do NOT
      // overwrite — the source_id test stops that race.
      if (prev.source_id !== match.source_id) return prev;
      if (prev.name === match.name) return prev;
      return { name: match.name, source_id: match.source_id };
    });
  }, [machineOptions, savedDataSource, widget.query?.source]);

  // Load the picked machine's nodes. The numeric-only list feeds the good /
  // total / metric-slot pickers; the wider numeric + string list feeds the
  // status + dynamic-row pickers. The unit references ride along so a per-row
  // unit autoset needs no second fetch.
  useEffect(() => {
    if (!machine) {
      setNumericNodeOptions([]);
      setNumericAndStringNodeOptions([]);
      setNodeUnitReferenceByNodeSourceId({});
      return;
    }
    setIsLoadingNodes(true);
    let cancelled = false;
    getActiveTimeseriesUseCase(machine.source_id).then((result) => {
      if (cancelled) return;
      if (result.success && result.data) {
        setNumericNodeOptions(toNumericNodeOptions(result.data.timeseries));
        setNumericAndStringNodeOptions(
          toNumericAndStringNodeOptions(result.data.timeseries),
        );
        setNodeUnitReferenceByNodeSourceId(
          Object.fromEntries(
            Object.values(result.data.timeseries)
              .filter((timeseries) => timeseries.data_type === "numeric")
              .map((timeseries) => [
                timeseries.source,
                getNumericNodeUnit(result.data.timeseries, timeseries.source),
              ]),
          ),
        );
      } else {
        setNumericNodeOptions([]);
        setNumericAndStringNodeOptions([]);
        setNodeUnitReferenceByNodeSourceId({});
        console.error("Failed to load nodes:", result.error);
      }
      setIsLoadingNodes(false);
    });

    return () => {
      cancelled = true;
    };
  }, [machine]);

  // Reveal the form once the machines list and the initial node load are done.
  useEffect(() => {
    if (!isInitializing) return;
    if (
      !isLoadingMachines &&
      !isLoadingUnits &&
      !(machine !== null && isLoadingNodes)
    ) {
      setIsInitializing(false);
    }
  }, [isLoadingMachines, isLoadingNodes, unitsStatus, machine, isInitializing]);

  // Bindings derive from the 3 fixed slots + the dynamic rows in editor order
  // + every metric's slots. Removed rows / removed metrics drop out of `rows`
  // / `metrics` so they also drop out of `bindings` and the renderer.
  const getSnapshot = () => {
    const fixedBindings: QueryBinding[] =
      MACHINE_OVERVIEW_FIXED_BINDING_KEYS.map((field) => ({
        field,
        source: machine?.source_id ?? "",
        address: nodeByRole[field]?.source_id ?? "",
      }));
    const rowBindings: QueryBinding[] = rows.map((row) => ({
      field: row.field,
      source: machine?.source_id ?? "",
      address: nodeByRole[row.field]?.source_id ?? "",
    }));
    // Metric slot bindings — flatten all metric cards into the shared bindings
    // list, one QueryBinding per (metric, slot) pair. A slot the user didn't
    // pick still rides along with an empty address; the renderer treats that as
    // "no value" (em-dash). Writing the empty shape lets the BE round-trip
    // cleanly without us stripping mid-state.
    const metricBindings: QueryBinding[] = metrics.flatMap((m) =>
      METRIC_SLOTS[m.kind].map((slot) => ({
        field: metricSlotField(m.id, slot),
        source: machine?.source_id ?? "",
        address: nodeByRole[metricSlotField(m.id, slot)]?.source_id ?? "",
      })),
    );
    return {
      bindings: [...fixedBindings, ...rowBindings, ...metricBindings],
      rows,
      rowLabels,
      statusEntries,
      statusDisplayMode,
      metrics: metrics.slice().sort((a, b) => a.order - b.order),
      transforms: mergeTransforms(
        widget.encoding?.transforms,
        ownedScaleFieldsRef.current,
        buildScaleTransforms([
          { field: "good", scale: scaleInputFor("good") },
          { field: "total", scale: scaleInputFor("total") },
          ...rows.map((row) => ({
            field: row.field,
            scale: scaleInputFor(row.field),
          })),
        ]),
      ),
      progressColorStart,
      progressColorEnd,
    };
  };

  // Save guard — title / machine / lookback first (the same order the drawer
  // used), then every card needs a node selection (removed cards are fine,
  // empty ones are not). First error wins, in card order.
  const validate = (): string | null => {
    if (title.trim() === "") return i18n(`${NS}.titleRequired`);
    if (!machine) return i18n(`${NS}.machineRequired`);
    if (lookbackMs == null) return i18n(`${NS}.lookbackRequired`);
    if (!nodeByRole.good?.source_id) return i18n(`${NS}.goodNodeRequired`);
    if (!nodeByRole.total?.source_id) return i18n(`${NS}.totalNodeRequired`);
    for (const row of rows) {
      if (!nodeByRole[row.field]?.source_id)
        return i18n(`${NS}.rowNodeRequired`);
    }
    // Scale inputs must be blank (default 1) or a real number. Zero is valid;
    // only unparseable text is rejected.
    for (const field of ["good", "total", ...rows.map((r) => r.field)]) {
      const raw = scaleInputFor(field);
      if (raw.trim() !== "" && parseUserNumber(raw) === null) {
        return i18n(`${NS}.scaleInvalid`);
      }
    }
    // Metric cards: every metric's REQUIRED slots must have a node picked
    // (cavities is optional for remaining_time, never required for quality —
    // REQUIRED_METRIC_SLOTS is the curated list). First missing slot wins, in
    // metric order.
    for (const metric of metrics) {
      if (metric.kind === "remaining_time" && metric.cycle_unit == null) {
        return i18n(`${NS}.metrics.cycleUnitRequired`);
      }
      for (const slot of REQUIRED_METRIC_SLOTS[metric.kind]) {
        const picked = nodeByRole[metricSlotField(metric.id, slot)];
        if (!picked?.source_id) {
          return i18n(`${NS}.metrics.${slot}NodeRequired`);
        }
      }
    }
    if (!nodeByRole.status?.source_id) return i18n(`${NS}.statusNodeRequired`);
    if (statusEntries.length === 0) return i18n(`${NS}.statusEntryRequired`);
    // Status mapping cards must carry a matchable value too. exact reads
    // entry.value; interval reads entry.min+entry.max. The first (unremovable)
    // card is included — an empty starter entry is a save-blocker, the user has
    // to fill it in.
    for (const entry of statusEntries) {
      // Label check runs first: an empty/whitespace label is the strictest
      // field (the value matcher below it could still be configured). Firing
      // the label warning first means the user fixes the name one card at a
      // time — same "first error wins" contract as the rows above.
      if (!entry.label.trim() && statusDisplayMode === "badge") {
        return i18n(`${NS}.statusLabelRequired`);
      }
      const issue = checkStatusEntryValue(entry);
      if (issue === "inverted") {
        return i18n(`${NS}.statusIntervalInvalid`);
      }
      if (issue === "missing") {
        return i18n(`${NS}.statusValueRequired`);
      }
    }
    return null;
  };

  const { save, isSaving } = useSaveWidget(widget, isPendingWidget, onClose);

  const handleSave = () => {
    const snapshot = getSnapshot();
    const updated: Widget = {
      ...widget,
      title: title.trim() || widget.title,
      // `imageFile === undefined` (Delete clicked) → null so the BE clears the
      // asset; otherwise pass through whatever the BE last round-tripped (`null`
      // for fresh widgets, a URL for uploaded ones).
      image_url: imageFile === undefined ? null : widget.image_url ?? null,
      query: {
        ...(widget.query ?? {
          source: machine?.source_id ?? "",
          shape: "scalar" as const,
          fields: {},
          sampling: { frequency: "raw", polling_interval: "5s" },
          bindings: [],
        }),
        source: machine?.source_id ?? widget.query?.source ?? "",
        bindings: snapshot.bindings,
        window: {
          duration: lookbackMs != null ? durationToIso(lookbackMs) : null,
        },
        sampling: {
          frequency: "raw",
          polling_interval: pollingInterval ?? DEFAULT_POLLING_INTERVAL,
        },
      },
      // The BE machine-overview model declares `status_entries` here and
      // nothing else — the bindings already travel through `query` above, and
      // a duplicate `semantics.bindings` would 422 as `extra_forbidden`.
      semantics: { status_entries: snapshot.statusEntries } as WidgetSemantics,
      encoding: {
        ...widget.encoding,
        rows: snapshot.rows,
        // Slot bindings are already written into `query.bindings` above. An
        // empty list is fine (no key leakage → BE accepts an empty array).
        metrics: snapshot.metrics,
        // Per-field scale factors. The snapshot already preserves unrelated
        // transforms; the key is omitted when the widget never had `transforms`
        // and nothing was set.
        ...transformsKey(snapshot.transforms, widget.encoding?.transforms),
      },
      presentation: {
        ...widget.presentation,
        row_labels: snapshot.rowLabels,
        // Set EXPLICITLY after the presentation spread so a previously-saved
        // "background" doesn't survive a flip back to badge in this PUT — the
        // conditional spread would have only added the key, leaving the stale
        // one in place. `undefined` is JSON-dropped, so badge mode emits a clean
        // body with no status_display_mode key at all.
        status_display_mode:
          snapshot.statusDisplayMode === "background"
            ? ("background" as const)
            : undefined,
        // Editor state is always a hex, so both keys carry an explicit value
        // and a re-save retrofits them onto legacy widgets in one step.
        progress_color_start: snapshot.progressColorStart,
        progress_color_end: snapshot.progressColorEnd,
        // Optional link to another dashboard. `undefined` is JSON-dropped, so
        // clearing the dropdown removes the key.
        dashboard_id: dashboardId ?? undefined,
      },
    };
    save(updated, { validate, imageFile });
  };

  const setRowLabel = (key: string, value: string): void => {
    setRowLabels((prev) => ({ ...prev, [key]: value }));
  };
  // Label setter for dynamic rows — keyed by row.id so the renderer's
  // resolveRowLabel picks up the user's edits. The field name is also
  // dropped (legacy fallback) so a re-open with synthesized legacy rows
  // still surfaces any old override.
  const setRowLabelByRow = (row: MachineOverviewRow, value: string): void => {
    setRowLabels((prev) => ({ ...prev, [row.id]: value }));
  };
  // Resolve a row's unit candidate from a picked node. Used at
  // checkbox-tick time only (the autoset effect below inlines its
  // own lookup so it can distinguish "no node" from "node without
  // unit ref" — a tick on a row whose picked node lacks a
  // `Timeseries.unit` falls through to `null` so the dropdown opens
  // empty and the user picks manually).
  const resolveAutoUnitForRow = (
    picked: MachineNameAndSourceId | null,
  ): string | null => {
    if (!picked) return null;
    const ref = nodeUnitReferenceByNodeSourceId[picked.source_id];
    if (ref == null) return null;
    return findUnitByReference(ref)?.unit_id ?? null;
  };
  // Tick/untick the row's "Show unit" checkbox. Tick with a
  // resolveable node → autoset; tick without → row.unit stays null
  // and the dropdown renders empty for the user to pick. Untick →
  // clear (the dropdown hides; the user can re-tick later).
  const toggleRowUnit = (rowId: string, ticked: boolean): void => {
    setUnitExpandedRows((prev) => {
      if (!ticked) {
        if (!(rowId in prev)) return prev;
        const next = { ...prev };
        delete next[rowId];
        return next;
      }
      return prev[rowId] ? prev : { ...prev, [rowId]: true };
    });
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        if (!ticked) return { ...row, unit: null };
        const picked = nodeByRole[row.field] ?? null;
        return { ...row, unit: resolveAutoUnitForRow(picked) };
      }),
    );
  };
  // Pick a unit from the dropdown (explicit user choice after
  // ticking without an autoset candidate). Bypasses autoset — the
  // user-picked value sticks until they change the node (which
  // triggers autoset on the new node's Timeseries.unit).
  const setRowUnitFromDropdown = (
    row: MachineOverviewRow,
    selected: MachineNameAndSourceId | null,
  ): void => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, unit: selected?.source_id ?? null } : r,
      ),
    );
  };
  // New card — unique OPC-UA-friendly field name (row_1, row_2, ...) so
  // bindings stay addressable; the row itself carries a stable random
  // UUID id that survives label renames. Label starts empty; the
  // placeholder guides the user.
  // Pick the next-order field name (mustn't clash with an existing
  // user-added row OR a legacy widget's template rows). The numeric
  // suffix is purely cosmetic (UI rounding) — the actual ordering
  // value rides on `order` (set right after).
  const addValueRow = (): void => {
    const existingNames = new Set(rows.map((r) => r.field));
    let nextN = rows.length + 1;
    while (existingNames.has(`row_${nextN}`)) nextN++;
    const field = `row_${nextN}`;
    // Shared insertion-order counter across rows + metrics so the
    // merged render view shows them in true chronological sequence
    // (a metric added today then a row tomorrow → row appears AFTER
    // the metric, not before). Both add handlers below read this
    // max + 1 from a single shared "next position" pool; using
    // separate counters per list would reset to 1 when one list was
    // empty and cause ordering conflicts on round-trip.
    const maxOrder = Math.max(
      0,
      ...rows.map((r) => r.order ?? 0),
      ...metrics.map((m) => m.order ?? 0),
    );
    const newRow: MachineOverviewRow = {
      id:
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      field,
      label: "",
      order: maxOrder + 1,
    };
    setRows((prev) => [...prev, newRow]);
    setNodeByRole(field, null);
    ownedScaleFieldsRef.current.add(field);
    // A fresh row starts at the default factor even if a dangling saved
    // transform happened to use the same generated field name.
    setScaleByField((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  // Remove a card — drop the row from the list + clear node pick (so a
  // later re-add doesn't resurrect stale data). Strips any id-keyed AND
  // legacy field-keyed label override so the rowLabel map doesn't
  // accumulate stale entries.
  const removeValueRow = (id: string): void => {
    const target = rows.find((r) => r.id === id);
    if (!target) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
    // Drop any unit-toggle / scale state the removed row carried, otherwise
    // the next "Add row" can reuse the same generated field name (row_N) and
    // inherit it.
    setUnitExpandedRows((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    // Drop any scale the removed row carried, otherwise the next
    // "Add row" can reuse the same generated field name (row_N) and
    // inherit the deleted row's multiplier.
    setScaleByField((prev) => {
      if (!(target.field in prev)) return prev;
      const next = { ...prev };
      delete next[target.field];
      return next;
    });
    setNodeByRoleRaw((prev) => {
      const next = { ...prev };
      delete next[target.field];
      return next;
    });
    setRowLabels((prev) => {
      const next = { ...prev };
      delete next[id];
      delete next[target.field];
      return next;
    });
  };
  // Add a metric card — pre-selects `remaining_time` so a freshly
  // added card is immediately actionable (3 required slots visible,
  // user picks nodes, done). Order = max(existing)+1 so a re-add after
  // a remove still appends to the bottom per user spec.
  // Shared insertion-order counter — same source as `addValueRow`
  // (a row added today then a metric tomorrow → metric appears AFTER
  // the row, not before). Reads `order` values from BOTH arrays so
  // the merged render-view stays in true chronological sequence.
  const addMetric = (): void => {
    const maxOrder = Math.max(
      0,
      ...rows.map((r) => r.order ?? 0),
      ...metrics.map((m) => m.order ?? 0),
    );
    const newMetric: MachineOverviewMetric = {
      id:
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `metric-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      kind: "remaining_time",
      order: maxOrder + 1,
    };
    setMetrics((prev) => [...prev, newMetric]);
  };
  // Flip a metric's kind — e.g. user added Remaining Time then
  // switched to Quality. The previously-picked slot bindings are
  // FLUSHED (every `metric_<id>_*` key on `nodeByRole` drops to
  // null). Reason: switching kind changes WHICH slots are valid
  // (Remaining Time has 4 slots, Quality has 2) — keeping picks
  // for slots the new kind doesn't read would let stale choices
  // leak into the saved widget. Flushing forces the user to
  // re-confirm each pick under the new formula. Same idea as a
  // form wizard that resets dependent fields when the parent
  // category changes.
  // Early-return guard: a same-kind re-click (someone opens the
  // dropdown and re-picks the already-selected item) is a no-op,
  // not a flush. Without this the user would silently lose every
  // node pick on every accidental click — user-data loss on a
  // factually harmless gesture.
  const changeMetricKind = (
    id: string,
    kind: MachineOverviewMetricKind,
  ): void => {
    setMetrics((prev) => {
      const current = prev.find((m) => m.id === id);
      if (!current || current.kind === kind) return prev;
      setNodeByRoleRaw((byRole) => {
        const next = { ...byRole };
        for (const key of Object.keys(next)) {
          if (key.startsWith(metricFieldPrefix(id))) {
            next[key] = null;
          }
        }
        return next;
      });
      // `cycle_unit` only applies to remaining_time — Quality is
      // unitless. Flush so when the user flips back to remaining_time
      // they must explicitly re-pick (no implicit default). The
      // edit-save path's validate() requires the new pick so leaving
      // it unset here is the correct "user hasn't decided" state.
      return prev.map((m) => {
        if (m.id !== id) return m;
        const updated: MachineOverviewMetric = { ...m, kind };
        if (kind !== "remaining_time") {
          delete updated.cycle_unit;
        }
        return updated;
      });
    });
  };
  // Pick the cycle-time unit for a remaining_time metric. Same-unit
  // re-pick is a no-op (the user can click the already-selected option
  // without side effects). The placeholder option (key `""`) re-clicks
  // here as a guard — we don't write empty strings back into the
  // metric record, which is the only state-set path that can leave
  // `cycle_unit` undefined between renders. Other kinds ignore this
  // handler at the JSX layer — the dropdown is only rendered when
  // `kind === "remaining_time"`.
  const changeMetricCycleUnit = (id: string, unit: string): void => {
    // Defensive guard at the handler so no future call site can leak
    // the empty-string placeholder into the metric record. The
    // validate path checks `cycle_unit == null` (which `""` would
    // fail) while `computeMetric`'s truthy check would treat `""` as
    // unset and render an em-dash — the wasted-pair would let a save
    // slip through with an unrenderable value. Refuse any value
    // outside the unit union at the registry boundary.
    if (
      unit !== "seconds" &&
      unit !== "milliseconds" &&
      unit !== "microseconds"
    )
      return;
    const u = unit as MachineOverviewMetricCycleUnit;
    setMetrics((prev) =>
      prev.map((m) =>
        m.id === id && m.cycle_unit !== u ? { ...m, cycle_unit: u } : m,
      ),
    );
  };
  // Remove a metric card — drop from the list, drop every
  // `metric_<id>_*` slot from `nodeByRole` so a re-add with the same
  // id doesn't resurrect dead picks. matches the row removal pattern
  // (node-pick + label cleanup in one pass).
  const removeMetric = (id: string): void => {
    setMetrics((prev) => prev.filter((m) => m.id !== id));
    setNodeByRoleRaw((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key.startsWith(metricFieldPrefix(id))) delete next[key];
      }
      return next;
    });
  };
  const setStatusEntry = (
    idx: number,
    entry: MachineOverviewStatusEntry,
  ): void => {
    setStatusEntries((prev) => prev.map((e, i) => (i === idx ? entry : e)));
  };
  const addStatusEntry = (): void => {
    setStatusEntries((prev) => [
      ...prev,
      { label: "", color_token: "white", mode: "exact" as const, value: "" },
    ]);
  };
  const removeStatusEntry = (idx: number): void => {
    setStatusEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const returnText = (index: number, translations: string): string =>
    i18n(translations).replace("{{ index }}", String(index));

  // Merged chronological render list — user-added rows + metric cards
  // interleaved by `order`. Rows without `order` (saved widgets
  // predating the field) sort to the top with `-Infinity` so the
  // legacy "rows first, metrics after" layout survives for them.
  // `useMemo` because the listed computation runs on every parent
  // render otherwise; sorting two short arrays isn't expensive but
  // dropping the JSX map recomputation keeps React 18 happy with
  // shallow reference stability on the children list.
  type DisplayItem =
    | { kind: "row"; row: MachineOverviewRow }
    | { kind: "metric"; metric: MachineOverviewMetric };
  const sortedDisplayItems: DisplayItem[] = useMemo(() => {
    return [
      ...rows.map((r) => ({
        kind: "row" as const,
        row: r,
        sortKey: r.order ?? Number.NEGATIVE_INFINITY,
      })),
      ...metrics.map((m) => ({
        kind: "metric" as const,
        metric: m,
        sortKey: m.order ?? Number.NEGATIVE_INFINITY,
      })),
    ].sort((a, b) => a.sortKey - b.sortKey);
  }, [rows, metrics]);

  // Row sequence map — cosmetic "Row N" labels need to count only
  // rows in the merged render (a metric interleaved between row_2
  // and row_3 doesn't bump any row's label). Recomputed alongside
  // sortedDisplayItems because it depends on the same ordering.
  const rowSequenceMap = useMemo(() => {
    const map: Record<string, number> = {};
    let counter = 0;
    for (const item of sortedDisplayItems) {
      if (item.kind === "row") {
        counter++;
        map[item.row.id] = counter;
      }
    }
    return map;
  }, [sortedDisplayItems]);

  // Render a single metric card. Pulled out of the merged map below
  // so the list-map doesn't have to handle JSX-comments + multi-return
  // branches + per-slot Fragment map + cycle-time unit selector inline
  // (which is fragile in JSX). Closure-bound to the live `nodeByRole`,
  // `metricSlotField`, etc. — re-creates on each render, but the
  // component tree is small enough that the JSX allocation cost is
  // negligible vs. the readability win.
  const renderMetricCard = (metric: MachineOverviewMetric): JSX.Element => {
    const requiredSlots = REQUIRED_METRIC_SLOTS[metric.kind];
    const allSlots = METRIC_SLOTS[metric.kind];
    return (
      <div
        key={metric.id}
        className={styles["machine-overview-editor__single-row-card"]}
      >
        <span className={styles["machine-overview-editor__field-label"]}>
          {i18n(`${NS}.metrics.metricKindLabel`)}
        </span>
        <MetricTypeDropdown
          ariaId={`metric-kind-${metric.id}`}
          value={metric.kind}
          options={[
            {
              key: "remaining_time",
              label: i18n(`${NS}.metrics.metricKind.remaining_time`),
            },
            {
              key: "quality",
              label: i18n(`${NS}.metrics.metricKind.quality`),
            },
          ]}
          onChange={(value: string) =>
            changeMetricKind(metric.id, value as MachineOverviewMetricKind)
          }
        />
        <p className={styles["machine-overview-editor__metric-formula"]}>
          {i18n(`${NS}.metrics.metricFormula.${metric.kind}`)}
        </p>
        {requiredSlots.map((slot) => (
          <React.Fragment key={slot}>
            <DashboardEditWidgetDrawerDropdown
              options={numericNodeOptions}
              selectedOption={
                nodeByRole[metricSlotField(metric.id, slot)] ?? null
              }
              onSelectOption={(n) => setMetricSlot(metric.id, slot, n)}
              labelText={i18n(`${NS}.metrics.metricSlot.${slot}`)}
              noOptionsText={i18n("modules.machineOverview.noNodes")}
              placeholderText={i18n("modules.machineOverview.nodesPlaceholder")}
              isLoading={isLoadingMachines}
            />
            {slot === "cycle_time" && metric.kind === "remaining_time" && (
              <>
                <span
                  className={styles["machine-overview-editor__field-label"]}
                >
                  {i18n(`${NS}.metrics.cycleUnitLabel`)}
                </span>
                <CycleTimeUnitDropdown
                  metric={metric}
                  ariaId={`metric-cycle-unit-${metric.id}`}
                  emptyLabel={i18n(`${NS}.metrics.cycleUnitPlaceholder`)}
                  options={[
                    {
                      key: "seconds",
                      label: i18n(`${NS}.metrics.cycleUnit.seconds`),
                    },
                    {
                      key: "milliseconds",
                      label: i18n(`${NS}.metrics.cycleUnit.milliseconds`),
                    },
                    {
                      key: "microseconds",
                      label: i18n(`${NS}.metrics.cycleUnit.microseconds`),
                    },
                  ]}
                  onChange={(value) => changeMetricCycleUnit(metric.id, value)}
                />
              </>
            )}
          </React.Fragment>
        ))}
        {allSlots
          .filter((s) => !requiredSlots.includes(s))
          .map((slot) => (
            <DashboardEditWidgetDrawerDropdown
              key={slot}
              options={numericNodeOptions}
              selectedOption={
                nodeByRole[metricSlotField(metric.id, slot)] ?? null
              }
              onSelectOption={(n) => setMetricSlot(metric.id, slot, n)}
              labelText={i18n(`${NS}.metrics.metricSlot.${slot}`).replace(
                "{{ optional }}",
                i18n(`${NS}.metrics.optionalSuffix`),
              )}
              noOptionsText={i18n("modules.machineOverview.noNodes")}
              placeholderText={i18n("modules.machineOverview.nodesPlaceholder")}
              isLoading={isLoadingMachines}
            />
          ))}
        <button
          type="button"
          className={styles["machine-overview-editor__remove-entry"]}
          onClick={() => removeMetric(metric.id)}
          aria-label={i18n(`${NS}.metrics.removeMetric`)}
        >
          <img src={closeIcon} alt="" />
        </button>
      </div>
    );
  };

  if (isInitializing) {
    return (
      <div className={styles["machine-overview-editor__loading"]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles["machine-overview-editor__root"]}>
      <Input2025
        inputPlaceHolder={i18n("modules.machineOverview.titleInputPlaceholder")}
        labelText={i18n("modules.machineOverview.titleLabel")}
        inputValue={title}
        onChangeFunc={(e) => setTitle(e.target.value)}
        inputVariant="dark-background"
      />

      <DashboardEditWidgetDrawerDropdown
        options={machineOptions}
        selectedOption={machine}
        onSelectOption={handleMachineChange}
        labelText={i18n("modules.machineOverview.machineLabel")}
        noOptionsText={i18n("modules.machineOverview.noMachines")}
        placeholderText={i18n("modules.machineOverview.machinesPlaceholder")}
        isLoading={isLoadingMachines}
      />

      {/* Section header above the per-role editors (cycles/progress/good/total
         card + dynamic row cards). The status section below uses its own
         header (statusMappingHeader). Both headers give the drawer two
         clearly labeled blocks. */}
      <h4 className={styles["machine-overview-editor__section-header"]}>
        {i18n(`${NS}.setupWidgetEntries`)}
      </h4>

      {/* Card 1 — cycles + progress labels stacked, then the two node
         pickers (good + total) below them — all grouped in one card. No
         internal Spacer / borders: the card's padding + gap handles
         spacing. */}

      <div className={styles["machine-overview-editor__two-row-card"]}>
        <Input2025
          inputPlaceHolder={i18n(`${NS}.cyclesLabel`)}
          labelText={i18n(`${NS}.cyclesLabel`)}
          inputValue={rowLabels.cycles ?? ""}
          onChangeFunc={(e) => setRowLabel("cycles", e.target.value)}
          inputVariant="dark-background"
        />
        <Input2025
          inputPlaceHolder={i18n(`${NS}.progressLabel`)}
          labelText={i18n(`${NS}.progressLabel`)}
          inputValue={rowLabels.progress ?? ""}
          onChangeFunc={(e) => setRowLabel("progress", e.target.value)}
          inputVariant="dark-background"
        />
        <DashboardEditWidgetDrawerDropdown
          options={numericNodeOptions}
          selectedOption={nodeByRole.good}
          onSelectOption={(n) => setNodeByRole("good", n)}
          labelText={i18n(`${NS}.nodeForGoodQuantity`)}
          noOptionsText={i18n("modules.machineOverview.noNodes")}
          placeholderText={i18n("modules.machineOverview.nodesPlaceholder")}
          isLoading={isLoadingMachines}
        />
        <ScaleInput
          value={scaleInputFor("good")}
          onChange={(raw) => setScaleFor("good", raw)}
        />
        <DashboardEditWidgetDrawerDropdown
          options={numericNodeOptions}
          selectedOption={nodeByRole.total}
          onSelectOption={(n) => setNodeByRole("total", n)}
          labelText={i18n(`${NS}.nodeForTotalQuantity`)}
          noOptionsText={i18n("modules.machineOverview.noNodes")}
          placeholderText={i18n("modules.machineOverview.nodesPlaceholder")}
          isLoading={isLoadingMachines}
        />

        <ScaleInput
          value={scaleInputFor("total")}
          onChange={(raw) => setScaleFor("total", raw)}
        />
        <ColorSwatchPicker
          label={i18n(`${NS}.progressColorStartLabel`)}
          value={progressColorStart}
          swatches={TREND_LINE_PALETTE}
          onChange={setProgressColorStart}
        />
        <ColorSwatchPicker
          label={i18n(`${NS}.progressColorEndLabel`)}
          value={progressColorEnd}
          swatches={TREND_LINE_PALETTE}
          onChange={setProgressColorEnd}
        />
        <button
          type="button"
          className={styles["machine-overview-editor__reset-progress-color"]}
          onClick={() => {
            setProgressColorStart(DEFAULT_PROGRESS_START_HEX);
            setProgressColorEnd(DEFAULT_PROGRESS_END_HEX);
          }}
        >
          {i18n(`${NS}.progressColorResetLabel`)}
        </button>
      </div>

      {/* User-added items: dynamic value rows + metric cards — interleaved
         in one chronological sequence so a metric added today and a row
         added tomorrow show the row AFTER the metric, not above (legacy
         behavior had two separate maps so insertion order was lost).
         Sort key = `order`; rows without `order` (saved widgets
         predating the field) sort to the top with `-Infinity` so the
         legacy "rows first, metrics after" layout survives for
         not-yet-saved widgets. Cards land ABOVE the shared
         add-buttons-row below so newly-added items never slip between
         the two buttons. */}
      {sortedDisplayItems.map((item) => {
        if (item.kind === "row") {
          const rowIndex = rowSequenceMap[item.row.id] ?? 1;
          const row = item.row;
          return (
            <div
              key={row.id}
              className={styles["machine-overview-editor__single-row-card"]}
            >
              <Input2025
                inputPlaceHolder={returnText(rowIndex, `${NS}.rowLabelMeta`)}
                labelText={returnText(rowIndex, `${NS}.rowLabelMeta`)}
                inputValue={
                  rowLabels[row.id] ?? rowLabels[row.field] ?? row.label ?? ""
                }
                onChangeFunc={(e) => setRowLabelByRow(row, e.target.value)}
                inputVariant="dark-background"
              />
              <DashboardEditWidgetDrawerDropdown
                options={numericAndStringNodeOptions}
                selectedOption={nodeByRole[row.field]}
                onSelectOption={(n) => setNodeByRole(row.field, n)}
                labelText={returnText(rowIndex, `${NS}.roleNodePlaceholder`)}
                noOptionsText={i18n("modules.machineOverview.noNodes")}
                placeholderText={i18n(
                  "modules.machineOverview.nodesPlaceholder",
                )}
                isLoading={isLoadingMachines}
              />
              <CheckBoxWithText
                text={i18n(`${NS}.rowShowUnitLabel`)}
                checkboxState={showsUnit(row)}
                handleCheckBoxClick={(_, ticked) =>
                  toggleRowUnit(row.id, ticked)
                }
              />
              {showsUnit(row) && (
                <DashboardEditWidgetDrawerDropdown
                  options={unitOptions}
                  selectedOption={
                    unitOptions.find((o) => o.source_id === row.unit) ?? null
                  }
                  onSelectOption={(n) => setRowUnitFromDropdown(row, n)}
                  labelText={i18n(`${NS}.rowUnitLabel`)}
                  noOptionsText={i18n("modules.machineOverview.noUnits")}
                  placeholderText={i18n(
                    "modules.machineOverview.unitsPlaceholder",
                  )}
                  isLoading={isLoadingMachines}
                />
              )}
              <ScaleInput
                value={scaleInputFor(row.field)}
                onChange={(raw) => setScaleFor(row.field, raw)}
              />
              <button
                type="button"
                className={styles["machine-overview-editor__remove-entry"]}
                onClick={() => removeValueRow(row.id)}
                aria-label={i18n(`${NS}.removeRowLabel`)}
              >
                <img src={closeIcon} alt="" />
              </button>
            </div>
          );
        }
        // Metric card — kind dropdown + per-slot node pickers +
        // optional cycle-time-unit selector for remaining_time.
        return renderMetricCard(item.metric);
      })}

      {/* Add buttons row — both "Add row" and "Add metric" share the
         same horizontal row at the bottom of the user-added blocks
         below the cycles/progress/good/total card. New cards (rows
         + metrics) are appended at the BOTTOM of their respective
         lists, ABOVE this row — so the buttons themselves stay
         between the user-added content and the status mapping
         section below. */}
      <div className={styles["machine-overview-editor__add-buttons-row"]}>
        <button
          type="button"
          className={styles["machine-overview-editor__add-status-button"]}
          onClick={addValueRow}
        >
          {i18n(`${NS}.addRowLabel`)}
        </button>
        <button
          type="button"
          className={styles["machine-overview-editor__add-status-button"]}
          onClick={addMetric}
        >
          {i18n(`${NS}.metrics.addMetricLabel`)}
        </button>
      </div>

      {/* Status mapping section — same label-then-picker pattern. */}
      <div className={styles["machine-overview-editor__status-section"]}>
        <h4 className={styles["machine-overview-editor__status-header"]}>
          {i18n(`${NS}.statusMappingHeader`)}
        </h4>
        <DashboardEditWidgetDrawerDropdown
          options={numericAndStringNodeOptions}
          selectedOption={nodeByRole.status}
          onSelectOption={(n) => setNodeByRole("status", n)}
          labelText={i18n(`${NS}.statusNodePlaceholder`)}
          noOptionsText={i18n("modules.machineOverview.noNodes")}
          placeholderText={i18n("modules.machineOverview.nodesPlaceholder")}
          isLoading={isLoadingMachines}
        />
        <div>
          <span className={styles["machine-overview-editor__toggle-label"]}>
            {i18n(`${NS}.statusDisplayModeLabel`)}
          </span>
          <div
            className={styles["machine-overview-editor__toggle"]}
            role="group"
            aria-label={i18n(`${NS}.statusDisplayModeLabel`)}
          >
            <button
              type="button"
              className={cn(styles["machine-overview-editor__toggle-button"], {
                [styles["machine-overview-editor__toggle-button--active"]]:
                  statusDisplayMode === "badge",
              })}
              onClick={() => setStatusDisplayMode("badge")}
            >
              {i18n(`${NS}.statusDisplayModeBadge`)}
            </button>
            <button
              type="button"
              className={cn(styles["machine-overview-editor__toggle-button"], {
                [styles["machine-overview-editor__toggle-button--active"]]:
                  statusDisplayMode === "background",
              })}
              onClick={() => setStatusDisplayMode("background")}
            >
              {i18n(`${NS}.statusDisplayModeBackground`)}
            </button>
          </div>
        </div>
        {statusEntries.map((entry, idx) => (
          <StatusRowEditor
            index={idx}
            key={`status-${idx}`}
            entry={entry}
            onChange={(next) => setStatusEntry(idx, next)}
            onRemove={() => removeStatusEntry(idx)}
            removable={idx > 0}
            displayMode={statusDisplayMode}
          />
        ))}
        <button
          type="button"
          className={styles["machine-overview-editor__add-status-button"]}
          onClick={addStatusEntry}
        >
          {i18n(`${NS}.addStatusMapping`)}
        </button>
      </div>

      <DashboardEditWidgetDrawerDropdown
        options={buildDurationOptions(GAUGE_LOOKBACK_MS, i18n)}
        selectedOption={
          lookbackMs != null
            ? findDurationOption(GAUGE_LOOKBACK_MS, lookbackMs, i18n)
            : null
        }
        onSelectOption={(s) => setLookbackMs(s ? Number(s.source_id) : null)}
        labelText={i18n("modules.machineOverview.lookbackLabel")}
        noOptionsText={i18n("modules.machineOverview.noLookbacks")}
        placeholderText={i18n("modules.machineOverview.lookbackPlaceholder")}
        isLoading={false}
      />
      <DashboardEditWidgetDrawerDropdown
        options={buildPollingOptions(i18n)}
        selectedOption={
          pollingInterval != null
            ? findPollingOption(pollingInterval, i18n)
            : null
        }
        onSelectOption={(s) => setPollingInterval(s ? s.source_id : null)}
        labelText={i18n("modules.machineOverview.pollingIntervalLabel")}
        noOptionsText={i18n("modules.machineOverview.noPollingIntervals")}
        placeholderText={i18n(
          "modules.machineOverview.pollingIntervalPlaceholder",
        )}
        isLoading={false}
      />

      {widget.image_url && imageFile === null ? (
        <div className={styles["machine-overview-editor__image-preview"]}>
          <img
            className={styles["machine-overview-editor__image-preview-img"]}
            src={widget.image_url}
            alt={i18n("modules.machineOverview.imageLabel")}
          />
          <IconButton
            variant="danger"
            icon={deleteRedIcon}
            fun={() => setImageFile(undefined)}
          />
        </div>
      ) : (
        <DropAreaAddImage
          label={i18n("modules.machineOverview.imageLabel")}
          values={{
            image: imageFile ?? null,
            imageFileName: imageFile?.name ?? "",
          }}
          keyProp="image"
          setValues={(v) => {
            // Replace-intent naturally trumps Delete: the picked File (or null
            // when cleared) overwrites any prior `undefined`.
            setImageFile(v.image as File | null | undefined);
          }}
        />
      )}

      <DashboardEditWidgetDrawerDropdown
        options={dashboardOptions}
        selectedOption={
          dashboardId
            ? dashboardOptions.find((o) => o.source_id === dashboardId) ?? {
                name: dashboardId,
                source_id: dashboardId,
              }
            : null
        }
        onSelectOption={(o) => setDashboardId(o?.source_id ?? null)}
        labelText={i18n("modules.machineOverview.dashboardLinkLabel")}
        noOptionsText={i18n("modules.machineOverview.noDashboards")}
        placeholderText={i18n(
          "modules.machineOverview.dashboardLinkPlaceholder",
        )}
        isLoading={false}
      />

      <Spacer16 />
      <FlexRowContainer>
        <ButtonWithLoader
          fun={handleSave}
          text={i18n("modules.machineOverview.saveButton")}
          loading={isSaving}
        />
        <Button
          fun={onClose}
          text={i18n("modules.machineOverview.cancelButton")}
          variant="secondary"
          // Cancel must NOT fire mid-save: it would dispatch removePendingWidget
          // while POST/PUT are still in flight and leak a server-side orphan.
          disabled={isSaving}
        />
      </FlexRowContainer>
    </div>
  );
};
